/**
 * @engine/input — Input Interaction
 *
 * Interactions define how control values trigger action phases.
 * Common uses: press, hold, multi-tap.
 */

import type { InteractionDef, InteractionType, ControlValue, ActionPhase, ActionContext } from '../types';

/**
 * Interaction state (persistent across updates)
 */
export interface InteractionState {
  /** Phase of this interaction */
  phase: 'waiting' | 'started' | 'performed' | 'canceled';
  /** Time when interaction started */
  startTime: number;
  /** Any additional state the interaction needs */
  [key: string]: unknown;
}

/**
 * Interface for input interactions
 */
export interface IInputInteraction {
  /** Interaction type identifier */
  readonly type: InteractionType;

  /**
   * Create initial state for this interaction
   */
  createState(): InteractionState;

  /**
   * Process an input value and return new action phase
   */
  process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    context: ActionContext
  ): ActionPhase;

  /**
   * Reset interaction state
   */
  reset(state: InteractionState): void;

  /**
   * Serialize to definition
   */
  toDefinition(): InteractionDef;
}

/**
 * Abstract base class for interactions
 */
export abstract class InputInteraction implements IInputInteraction {
  abstract readonly type: InteractionType;
  protected params: Record<string, unknown>;

  constructor(params: Record<string, unknown> = {}) {
    this.params = params;
  }

  createState(): InteractionState {
    return {
      phase: 'waiting',
      startTime: 0,
    };
  }

  abstract process(
    state: InteractionState,
    value: ControlValue,
    time: number,
    context: ActionContext
  ): ActionPhase;

  reset(state: InteractionState): void {
    state.phase = 'waiting';
    state.startTime = 0;
  }

  toDefinition(): InteractionDef {
    return {
      type: this.type,
      params: Object.keys(this.params).length > 0 ? { ...this.params } : undefined,
    };
  }

  /**
   * Helper: check if value is "active" (pressed, non-zero)
   */
  protected isActive(value: ControlValue): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return Math.abs(value) > 0.5;
    if (typeof value === 'object' && 'x' in value) {
      const vec = value as { x: number; y: number; z?: number };
      return Math.sqrt(vec.x * vec.x + vec.y * vec.y + (vec.z ?? 0) ** 2) > 0.5;
    }
    return false;
  }
}

/**
 * Interaction registry
 */
const interactionRegistry = new Map<InteractionType, new (params?: Record<string, unknown>) => IInputInteraction>();

/**
 * Register an interaction type
 */
export function registerInteraction(
  type: InteractionType,
  ctor: new (params?: Record<string, unknown>) => IInputInteraction
): void {
  interactionRegistry.set(type, ctor);
}

/**
 * Create an interaction from definition
 */
export function createInteraction(def: InteractionDef): IInputInteraction {
  const ctor = interactionRegistry.get(def.type);
  if (!ctor) {
    throw new Error(`Unknown interaction type: ${def.type}`);
  }
  return new ctor(def.params);
}
